import { useState, useEffect, useCallback } from 'react';
import {
    AccordionItem,
    AccordionHeader,
    AccordionBody,
    Button,
    Card,
    CardBody,
    Col,
    Row,
    FormGroup,
    Label,
    Input,
    CloseButton,
    Modal,
    ModalBody,
} from 'reactstrap';
import { Uploading } from '~/shared/components/Loading';
import { AlbumPhoto, PhotoAlbumDetail } from '~/types/campaign';
import {
    deleteCampaignPhoto,
    fetchCampaignPhotoPresignedUrl,
    uploadFile,
    saveAlbumPhoto,
    saveAlbumCaptions,
    saveAlbumName,
} from '~/services/campaign';
import { renderIcon } from '~/shared/components/icons';
import imageCompression from 'browser-image-compression';
import Dropzone from 'react-dropzone';

interface AlbumEditProps {
    album: PhotoAlbumDetail;
    index: number;
    toggle: (id: string) => void;
    isOpen: boolean;
    setDeleteAlbum: (album: PhotoAlbumDetail) => void;
    campaign_id: number;
    editMode: boolean;
    setEditMode: (editMode: boolean) => void;
}

interface PhotoCaption {
    caption: string;
    id: number;
}

export const AlbumEdit = ({
    album,
    index,
    toggle,
    isOpen,
    setDeleteAlbum,
    campaign_id,
    editMode,
    setEditMode,
}: AlbumEditProps) => {
    const [albumPhotos, setAlbumPhotos] = useState<AlbumPhoto[]>([]);
    const [photoCaptions, setPhotoCaptions] = useState<PhotoCaption[]>();
    const [coverPhotoId, setCoverPhotoId] = useState<number | null>(null);
    const [newAlbumName, setNewAlbumName] = useState('');
    const [publishGallery, setPublishGallery] = useState(false);
    const [deletePhoto, setDeletePhoto] = useState<number | null>(null);
    const [deletingPhoto, setDeletingPhoto] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (album) {
            setAlbumPhotos(album.photos);
            setNewAlbumName(album.name);
            setPublishGallery(album.published);
        }
    }, [album]);

    useEffect(() => {
        if (!albumPhotos || albumPhotos.length === 0) {
            return;
        }
        const captions = albumPhotos.map((a) => ({
            caption: a.caption,
            id: a.id,
        }));
        setPhotoCaptions(captions);
        let cover = albumPhotos.find((x) => x.is_album_cover);
        if (!cover) {
            cover = albumPhotos[0];
        }
        setCoverPhotoId(cover.id);
    }, [albumPhotos]);

    const toggleEdit = () => {
        const newEditMode = !editMode;
        setEditMode(newEditMode);
        if (!isOpen) {
            toggle(`album-${index}`);
        }
    };

    const resizeImage = async (file) => {
        const options = {
            maxSizeMB: 2,
            maxWidthOrHeight: 2000,
            useWebWorker: true,
        };
        const compressedFile = await imageCompression(file, options);
        return compressedFile;
    };

    const handleDelete = async (id) => {
        try {
            setDeletingPhoto(true);
            const resp = await deleteCampaignPhoto(id);
            if (resp.status === 204) {
                const newAlbumPhotos = albumPhotos.filter((x) => x.id !== id);
                setAlbumPhotos(newAlbumPhotos);
            }
            setDeletingPhoto(false);
            setDeletePhoto(null);
        } catch (e) {
            console.log('e: ', e);
            setDeletingPhoto(false);
            setDeletePhoto(null);
        }
    };

    const handleSaveCaptions = async () => {
        setSaving(true);
        const newPhotoCaptions = photoCaptions?.filter((x) => {
            const albumPhotoIndex = albumPhotos.findIndex(
                (photo) => photo.id === x.id
            );
            const existingCaption = albumPhotos[albumPhotoIndex].caption;
            if (existingCaption !== x.caption) {
                return true;
            }
        });

        // Determine if cover photo has changed
        const existingCover = albumPhotos.find((x) => x.is_album_cover);
        const updateCover = coverPhotoId !== existingCover?.id;

        if (
            albumPhotos.length > 0 &&
            ((newPhotoCaptions && newPhotoCaptions.length > 0) || updateCover)
        ) {
            const updatedPhotos = await saveAlbumCaptions(
                album.id,
                newPhotoCaptions,
                coverPhotoId
            );
            setAlbumPhotos(updatedPhotos);
        }

        // Save new album name
        if (newAlbumName !== album.name) {
            const payload = {
                album_id: album.id,
                name: newAlbumName,
                published: publishGallery,
            };
            await saveAlbumName(payload);
        } else if (publishGallery !== album.published) {
            const payload = {
                album_id: album.id,
                published: publishGallery,
            };
            await saveAlbumName(payload);
        }
        toggleEdit();
        setSaving(false);
    };

    const ImgDropzone = () => {
        const handleDrop = useCallback(
            (files) => {
                files.forEach(async (file) => {
                    setIsLoading(true);
                    const { fields, url } =
                        await fetchCampaignPhotoPresignedUrl(
                            file.name,
                            campaign_id
                        );
                    // Resize to max 2000 width
                    const resized = await resizeImage(file);
                    await uploadFile(fields, url, resized);
                    const { key } = fields;
                    const payload = {
                        album_id: album.id,
                        image: key,
                    };
                    const saveResp = await saveAlbumPhoto(payload);
                    setAlbumPhotos((prevPhotos) => [...prevPhotos, saveResp]);
                    setIsLoading(false);
                });
            },
            [campaign_id, album.id]
        );

        return (
            <Dropzone
                onDrop={handleDrop}
                accept={{ 'image/*': ['.jpeg', '.jpg', '.png', '.gif'] }}
            >
                {({ getRootProps, getInputProps }) => (
                    <Card
                        className='mb-4'
                        style={{ border: '2px dashed #949599' }}
                    >
                        <CardBody className='text-center'>
                            <div {...getRootProps()}>
                                <input {...getInputProps()} />
                                <div className='text-primary fs-4'>
                                    {isLoading ? (
                                        <Uploading />
                                    ) : (
                                        renderIcon('upload')
                                    )}
                                </div>
                                <p className='fs-6'>
                                    <strong>Click to upload</strong> or drag and
                                    drop
                                    <br />
                                    PNG, JPG, or GIF (max 2000 x 2000px)
                                </p>
                            </div>
                        </CardBody>
                    </Card>
                )}
            </Dropzone>
        );
    };

    const handleCaptionChange = (index, newVal) => {
        setPhotoCaptions((prevCaptions) => {
            const newCaptions = [...prevCaptions];
            newCaptions[index].caption = newVal;
            return newCaptions;
        });
    };

    // Helper function to get the most recent update date from album photos
    const getLastUpdatedDate = () => {
        if (!albumPhotos || albumPhotos.length === 0) {
            return null;
        }

        const dates = albumPhotos.map(photo => new Date(photo.update_date));
        const mostRecent = new Date(Math.max(...dates.map(date => date.getTime())));

        return mostRecent.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <>
            <AccordionItem>
                <AccordionHeader
                    targetId={`album-${index}`}
                    className={editMode ? 'border-primary' : 'w-100'}
                    onClick={() => {
                        toggle(`album-${index}`);
                        // if (!editMode) {
                        //   toggle(`album-${index}`);
                        // } else {
                        //   console.log("You're in edit mode, buddy");
                        // }
                    }}
                >
                    {newAlbumName || `Gallery ${index}`}
                </AccordionHeader>
                <AccordionBody
                    accordionId={`album-${index}`}
                    className={editMode ? 'border-primary' : ''}
                >
                    {getLastUpdatedDate() && (
                        <div className="mb-2">
                            Last updated: {getLastUpdatedDate()}
                        </div>
                    )}
                    <div className='mb-3 d-flex align-items-center'>
                        {editMode ? (
                            <>
                                <Input
                                    type='text'
                                    id='album-name'
                                    name='album-name'
                                    className='form-control-sm w-100 me-3'
                                    defaultValue={newAlbumName}
                                    required={true}
                                    aria-required={true}
                                    onChange={(e) =>
                                        setNewAlbumName(e.target.value)
                                    }
                                />
                                <Button
                                    color='outline-danger'
                                    size='sm'
                                    onClick={() => setDeleteAlbum(album)}
                                >
                                    {renderIcon('delete')}
                                    Delete
                                </Button>
                                <Button
                                    color='primary'
                                    size='sm'
                                    onClick={handleSaveCaptions}
                                    disabled={saving}
                                >
                                    {renderIcon('check')}
                                    {saving ? `Saving` : `Save`}
                                </Button>
                            </>
                        ) : (
                            <Button
                                color='outline-dark'
                                size='sm'
                                onClick={toggleEdit}
                            >
                                {/* {renderIcon('edit')} */}
                                Edit Gallery
                            </Button>
                        )}
                    </div>

                    {editMode && (
                        <FormGroup check className='mb-3'>
                            <Input
                                id='display-gallery'
                                type='checkbox'
                                defaultChecked={publishGallery}
                                onChange={() =>
                                    setPublishGallery(!publishGallery)
                                }
                            />{' '}
                            <Label check>
                                Display gallery on campaign page
                            </Label>
                        </FormGroup>
                    )}

                    {editMode ? <ImgDropzone /> : <div />}

                    <Row xs={2} md={3} lg={2} xl={4} className='g-4'>
                        {albumPhotos.map((photo: AlbumPhoto, i) => {
                            const caption = photoCaptions?.find(
                                (x) => x.id === photo.id
                            );
                            const imageUrl = photo.image.includes(
                                'media/campaign_pictures'
                            )
                                ? photo.image
                                : photo.image.replace(
                                    'campaign_pictures/',
                                    'media/campaign_pictures/'
                                );
                            return (
                                <Col key={i}>
                                    <div className='position-relative'>
                                        <img
                                            src={imageUrl}
                                            alt={photo.caption}
                                            className='w-100 object-fit-cover rounded-1'
                                            style={{ height: '160px' }}
                                        />
                                        {editMode ? (
                                            <>
                                                <CloseButton
                                                    onClick={() =>
                                                        setDeletePhoto(photo.id)
                                                    }
                                                    className='btn-sm position-absolute bg-white'
                                                    style={{
                                                        top: '10px',
                                                        right: '10px',
                                                    }}
                                                />
                                                <FormGroup
                                                    check
                                                    className='my-2 small'
                                                >
                                                    <input
                                                        type='radio'
                                                        className='form-check-input'
                                                        id={`${i}_is_album_cover`}
                                                        name={`album_cover_${album.id}`}
                                                        checked={
                                                            coverPhotoId === photo.id
                                                        }
                                                        onChange={() =>
                                                            setCoverPhotoId(
                                                                photo.id
                                                            )
                                                        }
                                                    />
                                                    <Label check>
                                                        Use as album cover
                                                    </Label>
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label className='mb-1 text-capitalize'>
                                                        Caption
                                                    </Label>
                                                    <Input
                                                        type='textarea'
                                                        bsSize='sm'
                                                        style={{
                                                            minHeight: 'unset',
                                                        }}
                                                        defaultValue={
                                                            caption
                                                                ? caption.caption
                                                                : ''
                                                        }
                                                        onChange={(e) =>
                                                            handleCaptionChange(
                                                                i,
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </FormGroup>
                                            </>
                                        ) : (
                                            <div className='small text-secondary'>
                                                {photo.caption}
                                            </div>
                                        )}
                                    </div>
                                </Col>
                            );
                        })}
                    </Row>
                </AccordionBody>
            </AccordionItem>

            <Modal
                centered
                isOpen={!!deletePhoto}
                toggle={() => setDeletePhoto(null)}
            >
                <ModalBody>
                    <h3 className='text-modal modal__title'>
                        Are you sure you want to delete this photo?
                    </h3>
                    <Button
                        color='outline-dark'
                        onClick={() => setDeletePhoto(null)}
                        disabled={deletingPhoto}
                    >
                        Cancel
                    </Button>
                    <Button
                        color='danger'
                        onClick={() => handleDelete(deletePhoto)}
                        disabled={deletingPhoto}
                    >
                        {deletingPhoto ? `Deleting...` : `Delete`}
                    </Button>
                </ModalBody>
            </Modal>
        </>
    );
};
